import React, { useState, useEffect, useCallback } from 'react';
import {
  Bot, Users, Activity, DollarSign, Loader2, Search, RefreshCw,
  TrendingUp, AlertTriangle, Shield, BookOpen, Save, X, Plus, Trash2, Edit3,
} from 'lucide-react';
import { aiSuperAdminApi } from './services/aiChatbotApi';
import { useToast } from '../email/shared/ToastProvider';
import './AIChatbot.css';

/**
 * SuperAdminAIConsole — Phase 11
 * Platform-wide visibility for MOS super admins:
 *  • Tab 1: Platform overview KPIs + top spenders
 *  • Tab 2: Customers table with token/cost drill-down
 *  • Tab 3: Skill marketplace (curated starter templates)
 */

const TABS = [
  { key: 'overview',    label: 'Platform Overview', icon: TrendingUp },
  { key: 'customers',   label: 'Customers',         icon: Users },
  { key: 'marketplace', label: 'Skill Marketplace', icon: BookOpen },
];

const WINDOWS = [
  { value: 1,  label: 'Last 24h' },
  { value: 7,  label: 'Last 7d' },
  { value: 30, label: 'Last 30d' },
  { value: 90, label: 'Last 90d' },
];

const fmtNum = (n) => Number(n || 0).toLocaleString();
const fmtINR = (n) => `₹${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const SuperAdminAIConsole = () => {
  const toast = useToast();
  const [tab, setTab]   = useState('overview');
  const [windowDays, setWindowDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const [overview,  setOverview]  = useState(null);
  const [topRows,   setTopRows]   = useState([]);
  const [customers, setCustomers] = useState([]);
  const [search,    setSearch]    = useState('');
  const [drill,     setDrill]     = useState(null);  // { companyId, data }
  const [marketplace, setMarketplace] = useState([]);
  const [editTpl, setEditTpl] = useState(null);

  /* ── Load overview + top ───────────────────────────────── */
  const loadOverview = useCallback(async () => {
    try {
      setLoading(true);
      const [ov, top] = await Promise.all([
        aiSuperAdminApi.platformOverview(windowDays),
        aiSuperAdminApi.topCompanies(windowDays, 10),
      ]);
      setOverview(ov.data?.entity || null);
      setTopRows(top.data?.entity || []);
    } catch (err) {
      toast.error('Failed to load platform overview');
    } finally {
      setLoading(false);
    }
  }, [windowDays, toast]);

  /* ── Load customers ─────────────────────────────────── */
  const loadCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const r = await aiSuperAdminApi.listCustomers(windowDays);
      setCustomers(r.data?.entity || []);
    } catch (err) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [windowDays, toast]);

  /* ── Load marketplace ───────────────────────────────── */
  const loadMarketplace = useCallback(async () => {
    try {
      setLoading(true);
      const r = await aiSuperAdminApi.marketplaceList();
      setMarketplace(r.data?.entity || []);
    } catch (err) {
      toast.error('Failed to load marketplace');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (tab === 'overview')    loadOverview();
    if (tab === 'customers')   loadCustomers();
    if (tab === 'marketplace') loadMarketplace();
  }, [tab, loadOverview, loadCustomers, loadMarketplace]);

  /* ── Drill into one customer ────────────────────────── */
  const openDrill = async (companyId) => {
    try {
      setLoading(true);
      const r = await aiSuperAdminApi.customerDetails(companyId, windowDays);
      setDrill({ companyId, data: r.data?.entity });
    } catch {
      toast.error('Failed to load customer details');
    } finally {
      setLoading(false);
    }
  };

  const updateBudget = async (companyId, body) => {
    try {
      await aiSuperAdminApi.setCustomerBudget(companyId, body);
      toast.success('Budget updated');
      if (drill?.companyId === companyId) await openDrill(companyId);
      await loadCustomers();
    } catch (err) {
      toast.error('Failed to update budget');
    }
  };

  /* ── Marketplace edit/save ──────────────────────────── */
  const saveTpl = async () => {
    const t = editTpl;
    if (!t.name || !t.system_prompt) return toast.error('Name and prompt are required');
    try {
      await aiSuperAdminApi.marketplaceUpsert(t);
      toast.success('Template saved');
      setEditTpl(null);
      await loadMarketplace();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    }
  };
  const deleteTpl = async (slug) => {
    if (!window.confirm(`Delete template "${slug}"? Tenants who already cloned it keep their copies.`)) return;
    try {
      await aiSuperAdminApi.marketplaceDelete(slug);
      toast.success('Template deleted');
      await loadMarketplace();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const filteredCustomers = customers.filter((c) =>
    !search.trim() || (c.company_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase())
  );

  /* ── Render ─────────────────────────────────────────── */
  return (
    <div className="ai-module">
      <div className="ai-module-header">
        <h1>
          <Shield size={26} color="#dc2626" />
          Super Admin · AI Console
        </h1>
        <p>Platform-wide visibility into AI usage, costs, customer budgets, and curated skill templates.</p>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8 }}>
          <nav className="ai-tabs" style={{ flex: 1 }}>
            {TABS.map((t) => (
              <button
                key={t.key}
                className={'ai-tab' + (tab === t.key ? ' active' : '')}
                onClick={() => setTab(t.key)}
                style={{ background: 'transparent', border: 'none' }}
              >
                <t.icon size={16} /> {t.label}
              </button>
            ))}
          </nav>
          {tab !== 'marketplace' && (
            <select
              className="ai-select"
              value={windowDays}
              onChange={(e) => setWindowDays(parseInt(e.target.value, 10))}
              style={{ width: 140 }}
            >
              {WINDOWS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
            </select>
          )}
          <button
            className="ai-btn ai-btn-secondary"
            onClick={() => {
              if (tab === 'overview') loadOverview();
              if (tab === 'customers') loadCustomers();
              if (tab === 'marketplace') loadMarketplace();
            }}
            disabled={loading}
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div className="ai-module-content">
        {loading && !overview && !customers.length && !marketplace.length && (
          <div className="ai-loader"><Loader2 size={20} className="spin" /> &nbsp;Loading…</div>
        )}

        {/* ── Overview tab ────────────────────────────── */}
        {tab === 'overview' && overview && (
          <>
            <div className="ai-stats">
              <div className="ai-stat">
                <div className="ai-stat-label">Active Customers</div>
                <div className="ai-stat-value">{fmtNum(overview.activeCustomers)}</div>
                <div className="ai-stat-sub">of {fmtNum(overview.totalCustomers)} total</div>
              </div>
              <div className="ai-stat">
                <div className="ai-stat-label">Tokens Consumed</div>
                <div className="ai-stat-value">{fmtNum(overview.tokensTotal)}</div>
                <div className="ai-stat-sub">In + out, {windowDays}d</div>
              </div>
              <div className="ai-stat">
                <div className="ai-stat-label">Platform Cost</div>
                <div className="ai-stat-value">{fmtINR(overview.costInr)}</div>
                <div className="ai-stat-sub">≈ ${Number(overview.costUsd || 0).toFixed(2)} USD</div>
              </div>
              <div className="ai-stat">
                <div className="ai-stat-label">AI Conversations</div>
                <div className="ai-stat-value">{fmtNum(overview.conversations)}</div>
                <div className="ai-stat-sub">{fmtNum(overview.aiTurns)} AI turns</div>
              </div>
            </div>

            <div className="ai-card">
              <h3 className="ai-card-title">
                <TrendingUp size={16} style={{ display: 'inline', marginRight: 6, color: '#4f46e5' }} />
                Top Spending Customers
              </h3>
              <div style={{ overflowX: 'auto' }}>
                <table className="ai-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                      <th style={{ padding: 10 }}>Customer</th>
                      <th style={{ padding: 10 }}>Tokens</th>
                      <th style={{ padding: 10 }}>Cost</th>
                      <th style={{ padding: 10 }}>AI Turns</th>
                      <th style={{ padding: 10 }}>Budget Use</th>
                      <th style={{ padding: 10 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {topRows.map((r) => {
                      const pct = r.monthly_token_budget > 0
                        ? Math.round((Number(r.tokens_used_this_period) / Number(r.monthly_token_budget)) * 100)
                        : 0;
                      return (
                        <tr key={r.company_id} style={{ borderTop: '1px solid #f1f5f9' }}>
                          <td style={{ padding: 10 }}>
                            <div style={{ fontWeight: 600 }}>{r.company_name || '—'}</div>
                            <div style={{ color: '#64748b', fontSize: 12 }}>{r.email || ''}</div>
                          </td>
                          <td style={{ padding: 10 }}>{fmtNum(r.tokens_total)}</td>
                          <td style={{ padding: 10 }}>{fmtINR(r.cost_inr)}</td>
                          <td style={{ padding: 10 }}>{fmtNum(r.ai_turns)}</td>
                          <td style={{ padding: 10, minWidth: 140 }}>
                            {r.monthly_token_budget > 0 ? (
                              <>
                                <div style={{ fontSize: 12, color: pct > 90 ? '#dc2626' : '#475569' }}>
                                  {pct}% of {fmtNum(r.monthly_token_budget)}
                                </div>
                                <div className="ai-usage-bar" style={{ marginTop: 4 }}>
                                  <div
                                    className={'ai-usage-fill ' + (pct > 90 ? 'danger' : pct > 70 ? 'warn' : '')}
                                    style={{ width: `${Math.min(pct, 100)}%` }}
                                  />
                                </div>
                              </>
                            ) : <span style={{ color: '#94a3b8' }}>unlimited</span>}
                          </td>
                          <td style={{ padding: 10 }}>
                            <button className="ai-btn ai-btn-secondary" onClick={() => openDrill(r.company_id)}>
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {topRows.length === 0 && (
                      <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>No data in this window.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ── Customers tab ───────────────────────────── */}
        {tab === 'customers' && (
          <div className="ai-card">
            <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <input
                  className="ai-input"
                  style={{ paddingLeft: 32 }}
                  placeholder="Search by company name or email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div style={{ alignSelf: 'center', color: '#64748b', fontSize: 13 }}>
                {filteredCustomers.length} customer{filteredCustomers.length === 1 ? '' : 's'}
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="ai-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                    <th style={{ padding: 10 }}>Company</th>
                    <th style={{ padding: 10 }}>Plan</th>
                    <th style={{ padding: 10 }}>Tokens (period)</th>
                    <th style={{ padding: 10 }}>Cost (window)</th>
                    <th style={{ padding: 10 }}>Skills</th>
                    <th style={{ padding: 10 }}>Last activity</th>
                    <th style={{ padding: 10 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((c) => (
                    <tr key={c.company_id} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: 10 }}>
                        <div style={{ fontWeight: 600 }}>{c.company_name || '—'}</div>
                        <div style={{ color: '#64748b', fontSize: 12 }}>{c.email || ''}</div>
                      </td>
                      <td style={{ padding: 10 }}>{c.plan_name || '—'}</td>
                      <td style={{ padding: 10 }}>
                        <div>
                          {fmtNum(c.tokens_used_this_period)}
                          {c.monthly_token_budget > 0 && (
                            <span style={{ color: '#64748b', fontSize: 12 }}>
                              {' '}/ {fmtNum(c.monthly_token_budget)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: 10 }}>{fmtINR(c.cost_inr_window)}</td>
                      <td style={{ padding: 10 }}>{c.active_skills_count ?? 0}</td>
                      <td style={{ padding: 10 }}>
                        {c.last_ai_at ? new Date(c.last_ai_at).toLocaleString() : '—'}
                      </td>
                      <td style={{ padding: 10 }}>
                        <button className="ai-btn ai-btn-secondary" onClick={() => openDrill(c.company_id)}>
                          Drill in
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filteredCustomers.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>
                      No customers match your filter.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Marketplace tab ─────────────────────────── */}
        {tab === 'marketplace' && (
          <div className="ai-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <h3 className="ai-card-title">
                  <BookOpen size={16} style={{ display: 'inline', marginRight: 6, color: '#4f46e5' }} />
                  Skill Marketplace
                </h3>
                <p className="ai-card-subtitle">
                  Curated starter templates that any tenant can clone into their own skills.
                </p>
              </div>
              <button className="ai-btn ai-btn-primary" onClick={() => setEditTpl({
                skill_key: '', name: '', description: '', category: 'industry',
                system_prompt: '', allowed_tools: [], kb_tags: [], is_active: true,
              })}>
                <Plus size={14} /> New Template
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
              {marketplace.map((t) => (
                <div key={t.skill_key} style={{
                  padding: 14, border: '1px solid #e2e8f0', borderRadius: 10, background: '#fafbff',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                    <div style={{ fontWeight: 700 }}>{t.name}</div>
                    <span className="ai-chip ai-chip-muted">{t.category}</span>
                  </div>
                  <div style={{ color: '#64748b', fontSize: 12, marginBottom: 6 }}>slug: {t.skill_key}</div>
                  <div style={{ color: '#475569', fontSize: 13, marginBottom: 10, minHeight: 36 }}>
                    {t.description || <em>No description</em>}
                  </div>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button className="ai-btn ai-btn-secondary" onClick={() => setEditTpl({ ...t })}>
                      <Edit3 size={12} /> Edit
                    </button>
                    <button className="ai-btn ai-btn-danger" onClick={() => deleteTpl(t.skill_key)}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
              {marketplace.length === 0 && !loading && (
                <div className="ai-help">No templates yet. Click <b>New Template</b> to create the first one.</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Drill-in modal ─────────────────────────────── */}
      {drill && drill.data && (
        <div onClick={() => setDrill(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 12, width: '100%', maxWidth: 720, maxHeight: '88vh',
            overflowY: 'auto', padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ margin: 0 }}>{drill.data.company_name || 'Customer'}</h2>
                <div style={{ color: '#64748b', fontSize: 13 }}>{drill.data.email || ''}</div>
              </div>
              <button className="ai-btn ai-btn-secondary" onClick={() => setDrill(null)}>
                <X size={14} />
              </button>
            </div>

            <div className="ai-stats" style={{ marginTop: 16 }}>
              <div className="ai-stat">
                <div className="ai-stat-label">Plan</div>
                <div className="ai-stat-value" style={{ fontSize: 16 }}>{drill.data.plan_name || '—'}</div>
              </div>
              <div className="ai-stat">
                <div className="ai-stat-label">Tokens (period)</div>
                <div className="ai-stat-value" style={{ fontSize: 16 }}>
                  {fmtNum(drill.data.tokens_used_this_period)}
                  {drill.data.monthly_token_budget > 0 && (
                    <span style={{ fontSize: 13, color: '#94a3b8' }}>
                      {' '}/ {fmtNum(drill.data.monthly_token_budget)}
                    </span>
                  )}
                </div>
              </div>
              <div className="ai-stat">
                <div className="ai-stat-label">Cost ({windowDays}d)</div>
                <div className="ai-stat-value" style={{ fontSize: 16 }}>{fmtINR(drill.data.cost_inr_window)}</div>
              </div>
            </div>

            <div className="ai-card" style={{ marginTop: 16 }}>
              <h3 className="ai-card-title">
                <Activity size={14} style={{ display: 'inline', marginRight: 6, color: '#4f46e5' }} />
                Adjust Monthly Budget
              </h3>
              <BudgetEditor
                initial={drill.data.monthly_token_budget || 0}
                onSave={(tokens) => updateBudget(drill.companyId, { tokens })}
              />
            </div>

            {Array.isArray(drill.data.skills) && drill.data.skills.length > 0 && (
              <div className="ai-card" style={{ marginTop: 16 }}>
                <h3 className="ai-card-title">Active Skills</h3>
                {drill.data.skills.map((s) => (
                  <div key={s.skill_key} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <b>{s.name}</b> <span style={{ color: '#64748b', fontSize: 12 }}>({s.skill_key})</span>
                    {typeof s.usage_count === 'number' && (
                      <span style={{ marginLeft: 8, color: '#475569', fontSize: 12 }}>
                        used {fmtNum(s.usage_count)} times
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {Array.isArray(drill.data.recentTurns) && drill.data.recentTurns.length > 0 && (
              <div className="ai-card" style={{ marginTop: 16 }}>
                <h3 className="ai-card-title">Recent AI Turns</h3>
                <div style={{ maxHeight: 280, overflowY: 'auto', fontSize: 12 }}>
                  {drill.data.recentTurns.map((t, i) => (
                    <div key={i} style={{ padding: 8, borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{ color: '#64748b' }}>
                        {new Date(t.created_at).toLocaleString()} · {t.tokens_total} tokens · {fmtINR(t.cost_inr)}
                      </div>
                      <div style={{ marginTop: 2 }}><b>U:</b> {(t.user_message || '').slice(0, 140)}</div>
                      <div style={{ marginTop: 2 }}><b>AI:</b> {(t.ai_response || '').slice(0, 140)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Marketplace template editor ────────────────── */}
      {editTpl && (
        <div onClick={() => setEditTpl(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 12, width: '100%', maxWidth: 760, maxHeight: '88vh',
            overflowY: 'auto', padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0 }}>{editTpl.id ? 'Edit template' : 'New template'}</h2>
              <button className="ai-btn ai-btn-secondary" onClick={() => setEditTpl(null)}>
                <X size={14} />
              </button>
            </div>

            <div className="ai-form-row" style={{ marginTop: 16 }}>
              <div className="ai-form-group">
                <label className="ai-label">Slug *</label>
                <input
                  className="ai-input"
                  placeholder="e.g. real_estate_agent"
                  value={editTpl.skill_key}
                  disabled={!!editTpl.id}
                  onChange={(e) => setEditTpl({ ...editTpl, skill_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                />
              </div>
              <div className="ai-form-group">
                <label className="ai-label">Category</label>
                <select
                  className="ai-select"
                  value={editTpl.category}
                  onChange={(e) => setEditTpl({ ...editTpl, category: e.target.value })}
                >
                  {['industry', 'support', 'sales', 'marketing', 'operations', 'platform'].map((c) =>
                    <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div className="ai-form-group">
              <label className="ai-label">Display name *</label>
              <input
                className="ai-input"
                value={editTpl.name}
                onChange={(e) => setEditTpl({ ...editTpl, name: e.target.value })}
              />
            </div>

            <div className="ai-form-group">
              <label className="ai-label">Description</label>
              <input
                className="ai-input"
                value={editTpl.description}
                onChange={(e) => setEditTpl({ ...editTpl, description: e.target.value })}
              />
            </div>

            <div className="ai-form-group">
              <label className="ai-label">System prompt *</label>
              <textarea
                className="ai-input"
                rows={10}
                style={{ fontFamily: 'monospace', fontSize: 13 }}
                value={editTpl.system_prompt}
                onChange={(e) => setEditTpl({ ...editTpl, system_prompt: e.target.value })}
              />
            </div>

            <div className="ai-form-row">
              <div className="ai-form-group">
                <label className="ai-label">Allowed tools (comma-separated)</label>
                <input
                  className="ai-input"
                  value={(editTpl.allowed_tools || []).join(', ')}
                  onChange={(e) => setEditTpl({
                    ...editTpl,
                    allowed_tools: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  })}
                />
              </div>
              <div className="ai-form-group">
                <label className="ai-label">KB tags (comma-separated)</label>
                <input
                  className="ai-input"
                  value={(editTpl.kb_tags || []).join(', ')}
                  onChange={(e) => setEditTpl({
                    ...editTpl,
                    kb_tags: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  })}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="ai-btn ai-btn-secondary" onClick={() => setEditTpl(null)}>Cancel</button>
              <button className="ai-btn ai-btn-primary" onClick={saveTpl}>
                <Save size={14} /> Save Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BudgetEditor = ({ initial, onSave }) => {
  const [val, setVal] = useState(initial);
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
      <div className="ai-form-group" style={{ flex: 1 }}>
        <label className="ai-label">Monthly token budget</label>
        <input
          className="ai-input"
          type="number" min="0" step="1000"
          value={val}
          onChange={(e) => setVal(parseInt(e.target.value || '0', 10))}
        />
        <div className="ai-help">Set 0 for unlimited.</div>
      </div>
      <button className="ai-btn ai-btn-primary" onClick={() => onSave(val)}>
        <Save size={14} /> Apply
      </button>
    </div>
  );
};

export default SuperAdminAIConsole;
